import type { PrintSource } from '../../../utilities/printCropGeometry';
import { calculatePNGMaskPositioning } from './pngProcessor';

/** The map files the editor is handed (useViewModel.ts:247-251); they keep today's crop. */
const MAP_FILE_NAMES = ['star-map.png', 'location-map.png'];

export interface CreatePrintSourceOptions {
  /** The customer's original upload. */
  file: File;
  maskBlob: Blob | null;
  uploadType: 'photo' | 'engraving';
  /** Whether the save treats this image as a map. */
  isMapImage: boolean;
  /** Natural size of the original. */
  image: { width: number; height: number };
  canvasWidth: number;
  canvasHeight: number;
  drawWidth: number;
  drawHeight: number;
  rotation: number;
  scale: number;
  flipX: boolean;
  offsetX: number;
  offsetY: number;
  positioning?: { x: number; y: number; scale: number; rotation: number };
}

/**
 * What the print crop needs to cut the customer's original upload exactly as the editor framed it
 * (ALBAU-922): the original, the mask, and the numbers the save drew the photo with. Undefined for
 * uploads that keep today's crop: engravings, star and location maps, and unmasked uploads.
 */
export function createPrintSource(options: CreatePrintSourceOptions): PrintSource | undefined {
  const { file, maskBlob, uploadType, isMapImage, image, canvasWidth, canvasHeight, positioning } = options;
  if (!maskBlob || uploadType !== 'photo' || isMapImage || MAP_FILE_NAMES.includes(file.name)) return undefined;

  // The renderer's own placement (canvasRenderer.ts:287-291, pngProcessor.ts:222), so the numbers match.
  const mask = calculatePNGMaskPositioning({ containerWidth: canvasWidth, containerHeight: canvasHeight, positioning });

  return {
    file,
    maskBlob,
    transform: {
      canvasWidth,
      canvasHeight,
      imageWidth: image.width,
      imageHeight: image.height,
      drawWidth: options.drawWidth,
      drawHeight: options.drawHeight,
      rotation: options.rotation,
      scale: options.scale,
      flipX: options.flipX,
      offsetX: options.offsetX,
      offsetY: options.offsetY,
      maskCenterX: mask.maskCenterX,
      maskCenterY: mask.maskCenterY,
      maskWidth: canvasWidth * mask.maskScalePercent
    }
  };
}
